import * as React from "react";
import {configStorage} from "../../../../../../ConfigStorage";
import {CompanyConfiguration, GenericXlsConverterConfiguration,} from "../../../../../../generated";
import {ConfigContext} from "../../../../../main/context/ConfigContext";
import {useDispatch, useSelector} from "react-redux";
import {
    getActiveConfig,
    getActiveMode,
    getActiveRules,
    getParseOptions,
    getSaveToConfig,
    getSpecificActiveRules,
    getSplitterOptions, ParserOptions,
    selectRules,
    setSaveToConfig
} from "../../slices/ruleSlice";
import "./AutoSaveRulesToConfig.css"
import {updateConfig, workingConfig} from "../../../../../main/pages/configChange/ConfigSignal";


interface LoadRulesFromConfigButtonProps {
}

function sanitizeSplitterOptions<T extends object>(patch?: Partial<T>): Partial<T> {
    if (!patch) return {};
    const out: any = {};
    for (const [k, v] of Object.entries(patch)) {
        if (k === "mode") continue;
        if (v !== undefined) out[k] = v;
    }
    return out;
}

function mergeDefined<T extends object>(base: T | undefined, patch?: Partial<T>): T {
    const out: any = { ...(base ?? {}) };
    if (patch) {
        for (const [k, v] of Object.entries(patch)) {
            if (v !== undefined) out[k] = v;
        }
    }
    return out as T;
}

export function AutoSaveRulesToConfig(props: React.PropsWithChildren<LoadRulesFromConfigButtonProps>) {
    const dispatch = useDispatch()
    const context = React.useContext(ConfigContext)
    const [config, setConfig] = React.useState<CompanyConfiguration>(null)
    const rules = useSelector(selectRules)
    const options = useSelector(getParseOptions)
    const activeConfig = useSelector(getActiveConfig)
    const activeRules = useSelector(getActiveRules)
    const specificActiveRules = useSelector(getSpecificActiveRules)
    const activeMode = useSelector(getActiveMode)
    const updatedSplitterOptions = useSelector(getSplitterOptions)
    const autoSaveConfig = useSelector(getSaveToConfig)
    
    // Track what sections have changed to enable selective saving
    const [changedSections, setChangedSections] = React.useState<{
        parseOptions?: boolean;
        splitterOptions?: boolean;
        rules?: boolean;
        activeMode?: boolean;
    }>({})

    React.useEffect(() => {
        // console.log("AutoSaveRulesToConfig useEffect");
        if (workingConfig.value) {
            setConfig(JSON.parse(workingConfig.value));
        } else if (context.companyId) {
            // console.log("Fetching config from configStorage");
            configStorage.getElement(context.companyId).then((c) => {
                setConfig(c.config);
                updateConfig(JSON.stringify(c.config, null, 4), false, false);
            }).catch(error => {
                console.error("Error fetching config from configStorage:", error);
            });
        }
    }, [context.companyId]); // added context.companyId to the dependency array


   const [prevOptions, setPrevOptions] = React.useState(options)
    const [prevSplitterOptions, setPrevSplitterOptions] = React.useState(updatedSplitterOptions)
    const [prevActiveMode, setPrevActiveMode] = React.useState(activeMode)
    
    React.useEffect(() => {
        if (JSON.stringify(prevOptions) !== JSON.stringify(options)) {
            setChangedSections(prev => ({ ...prev, parseOptions: true }))
            setPrevOptions(options)
        }
    }, [options, prevOptions])
    
    React.useEffect(() => {
        if (JSON.stringify(prevSplitterOptions) !== JSON.stringify(updatedSplitterOptions)) {
            setChangedSections(prev => ({ ...prev, splitterOptions: true }))
            setPrevSplitterOptions(updatedSplitterOptions)
        }
    }, [updatedSplitterOptions, prevSplitterOptions])
    
    React.useEffect(() => {
        if (prevActiveMode !== activeMode) {
            setChangedSections(prev => ({ ...prev, activeMode: true }))
            setPrevActiveMode(activeMode)
        }
    }, [activeMode, prevActiveMode])

    React.useEffect(() => {
        if (autoSaveConfig){
            saveToConfig().then(() => {
                dispatch(setSaveToConfig(false))
            })
        }
    }, [autoSaveConfig])

    async function saveToConfig() {
           if (!config) {
            console.error('No config available.');
            return;
        }

        // Check the loaded config name
        if (activeConfig === "default") {
            // Default config saving logic
            if (!config.genericXlsConfiguration) {
                config.genericXlsConfiguration = {} as GenericXlsConverterConfiguration;
            }
            
            // Only update sections that have changed
            if (changedSections.activeMode) {
                config.genericXlsConfiguration.inputFormat = activeMode
            }

            if (changedSections.splitterOptions) {
                const patch = sanitizeSplitterOptions(updatedSplitterOptions);
                // Only update if there are actual changes to save
                if (Object.keys(patch).length > 0) {
                    config.genericXlsConfiguration.splitterOptions = mergeDefined(
                        config.genericXlsConfiguration.splitterOptions,
                        patch
                    );
                }
            }

            // Always save rules when autoSaveConfig is triggered (manual save via Save button)
            if (activeRules == "ext") {
                config.genericXlsConfiguration.extractionRules = JSON.parse(JSON.stringify(rules));
            }

            if (activeRules == "spt") {
                config.genericXlsConfiguration.splittingRules = JSON.parse(JSON.stringify(rules));
            }

            if (changedSections.parseOptions) {
                // Update immediate format parser options
                Object.keys(options).forEach(key => {
                    if (options[key]) {
                        if (!config.genericXlsConfiguration.immediateFormatParserOptions) {
                            config.genericXlsConfiguration.immediateFormatParserOptions = {
                                dateFormat: null,
                                timeFormat: null,
                                numberFormat: null,
                                weightUnit: null,
                                lengthUnit: null,
                                defaultArriveHour: null,
                                defaultDepartHour: null,
                                stopListMode: null,
                                aiPompts: null,
                                genericAiPromptAdditionalInstructions: undefined
                            }
                        }
                        if (config.genericXlsConfiguration.immediateFormatParserOptions[key] !== options[key]) {
                            config.genericXlsConfiguration.immediateFormatParserOptions[key] = options[key];
                        }
                    }
                });
            }
        } else {
            // Specific config saving logic - only update changed sections
            config.genericXlsConfiguration?.specificConfiguration?.forEach(sc => {
                if (sc.verboseName == activeConfig) {
                    if (specificActiveRules == "spcExt") {
                        // Only update sections that have changed
                        if (changedSections.parseOptions) {
                            sc.override = {
                                ...sc.override,
                                immediateFormatParserOptions: JSON.parse(JSON.stringify(options))
                            };
                        }
                        
                        if (changedSections.splitterOptions) {
                            const patch = sanitizeSplitterOptions(updatedSplitterOptions);
                            if (Object.keys(patch).length > 0) {
                                sc.override = {
                                    ...sc.override,
                                    splitterOptions: mergeDefined(
                                        sc.override?.splitterOptions,
                                        patch
                                    )
                                };
                            }
                        }
                        
                        if (changedSections.activeMode) {
                            sc.override = {
                                ...sc.override,
                                inputFormat: activeMode
                            };
                        }
                        
                        // Always save rules when autoSaveConfig is triggered (manual save via Save/Remove button)
                        sc.override = {
                            ...sc.override,
                            extractionRules: JSON.parse(JSON.stringify(rules))
                        };
                    } else {
                        // Always save rules when autoSaveConfig is triggered (manual save via Save/Remove button)
                        sc.applyWhen = {
                            ...sc.applyWhen,
                            onRuleMatch: JSON.parse(JSON.stringify(rules)),
                        }
                    }
                }
            })
        }

        await updateConfig(JSON.stringify(config, null, 4), true, false);
        
        // Reset changed sections after successful save
        setChangedSections({})
    }
    // setTimeout(()=>saveToConfig(),1000)
    return <></>
}